import { useEffect, useRef, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useHistory } from 'react-router-dom';
import { Workflow } from '../../types';
import { useApplicationStore } from '../home/ApplicationStore';
import { useChannelMessagingStore } from '../../Store/useChannelMessagingStore';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/useTheme';
import { IWebView, useWebViewStore } from '../webview/useWebViewStore';
import { useIPRestrictionStore } from '../dealer-admin/ip-access/IPRestrictionStore';
import useAudit from '../../hooks/useAudit';
import {
  MESSAGES,
  LOGOUT_STATUS,
  MessageContext,
} from './channelMessage.types';
import {
  resolveTargetOrigin,
  urlsMatch,
  extractPathFromUrl,
  extractOriginFromUrl,
  getRestoreUrlForView,
  pathToBreadcrumb,
  readOktaTokenStorage,
  isReloadNavigation,
  postTokenMessage,
} from './channelMessage.utils';
import { useChannelMessageApps } from '../core/useChannelMessageApps';

export { useChannelMessage } from './channelMessage.types';
export { MESSAGES, LOGOUT_STATUS } from './channelMessage.types';

export default function ChannelMessageProvider(props: any) {
  const unifyOrigin = window.location.origin;
  const { applications } = useApplicationStore();
  const { data: channelMsgApps } = useChannelMessageApps();
  const { i18n } = useTranslation();
  const { darkMode } = useTheme();
  const { getActiveIframe, webviews, setVisitedLocation, resetActive ,setIndex} = useWebViewStore();
  const { oktaAuth } = useOktaAuth() || {};
  const [logoutStatus, setLogoutStatus] = useState<LOGOUT_STATUS>(LOGOUT_STATUS.NONE);
  const history = useHistory();
  const { captureEvent } = useAudit();
  const appDomains = useChannelMessagingStore((state: any) => state.appDomains);
  const setAppDomains = useChannelMessagingStore((state: any) => state.setAppDomains);
  const rightClickStack  = useChannelMessagingStore((state: any) => state.rightClickStack);
  const setRightClickStack = useChannelMessagingStore((state: any) => state.setRightClickStack);
  const ipRestriction = useIPRestrictionStore((state: any) => state.ipRestriction);


  const notifyMessageRef = useRef<(action: string, payload: any) => void>(() => {});
  const webviewsRef = useRef(webviews);
  const refreshNotifiedRef = useRef<Set<string>>(new Set());
  const lastReportedUrlRef = useRef<Record<string, string>>({});
  const listenersMap = useRef<Record<string, (evt: MessageEvent) => void>>({});

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const channelMessageEnabled = (id: string): boolean => {
    if (!id || !channelMsgApps || !Array.isArray(channelMsgApps)) return false;
    return channelMsgApps.some((app: any) => app.appId === id);
  };

  const getWorkflowAppId = (wid: string): string => {
    const all = applications.reduce((acc: any, app: any) => acc.concat(app.workflows), []);
    const wf = all?.find((w: Workflow & { workflowId?: string }) => w?.workflowId === wid || w?.id === wid);
    return wf?.appId || wid;
  };

  const getIPRestrictionsWithLabel = (iprs: any[]) => {
    const workflows = applications.reduce((acc: any, app: any) => acc.concat(app.workflows), []);
    try {
      return iprs
        .filter((ip) => ip.entityType === 'workflow' && ip.isRestricted)
        .map((ip) => ({ ...ip, workflow: workflows?.find((wf: any) => wf.workflowId === ip.entityId)?.name }));
    } catch (e) {
      console.warn('ERROR in getIPRestrictionsWithLabel', e);
    }
  };

  /**
   * Resolves the actual iframe element registered for a given app id and
   * verifies that a received MessageEvent genuinely originated from that
   * iframe's contentWindow. This is the source-of-truth check — evt.data
   * fields (id, origin, etc.) are attacker-controllable and must never be
   * trusted on their own to authorize an action.
   */
  const getVerifiedFrame = (id: string, evt: MessageEvent): HTMLIFrameElement | null => {
    if (!id) return null;
    const frameEl = document.getElementById(id) as HTMLIFrameElement | null;
    if (!frameEl) {
      console.warn(`[cm] Rejected message — no registered iframe for id "${id}"`);
      return null;
    }
    if (evt.source !== frameEl.contentWindow) {
      console.warn(`[cm] Rejected message for "${id}" — event source did not match the registered iframe's window`);
      return null;
    }
    return frameEl;
  };

  // ─── Messaging primitives ───────────────────────────────────────────────────

  const postMessage = (frm: HTMLIFrameElement, args: any, origin: string): void => {
    if (!channelMessageEnabled(getWorkflowAppId(args.id))) return;
    const targetOrigin = resolveTargetOrigin(appDomains[args?.id || ''] || origin || frm?.src);
    if (!targetOrigin) return;
    frm?.contentWindow?.postMessage({ ...args }, targetOrigin);
  };

  const notifyMessage = (action: string, payload: any): void => {
    webviews.forEach((wv: IWebView) => {
      const frmEl = document.getElementById(wv.id) as HTMLIFrameElement;
      const appOrigin = appDomains[wv.id!] || wv.url;
      if (payload?.renewed) {
        frmEl?.contentWindow?.postMessage({ id: wv.id, action, payload, origin: unifyOrigin }, appOrigin);
      } else {
        frmEl?.contentWindow?.postMessage(payload, appOrigin);
      }
    });
  };
  notifyMessageRef.current = notifyMessage;

  const addMessageListener = (src: string, listener: (evt: MessageEvent) => void): void => {
    listenersMap.current[src] = listener;
  };

  const initClient = (frm: HTMLIFrameElement, id: string, url: string): void => {
    const targetOrigin = resolveTargetOrigin(url);
    if (!targetOrigin) return;
    postMessage(frm, {
      id,
      action: MESSAGES.UNIFY_CLIENT_CONNECT,
      value: {
        ipRestriction: getIPRestrictionsWithLabel(ipRestriction),
        product: { id, origin: url, unifyOrigin: document.location.href },
        user: { preferences: { language: i18n.language, theme: darkMode ? 'dark' : 'light' } },
      },
    }, targetOrigin);
  };

  const refreshActiveFrame = (): void => {
    const activeView = getActiveIframe();
    const activeFrm: any = document.getElementById(activeView?.id || '');
    postMessage(activeFrm, { id: activeView?.id, action: MESSAGES.UNIFY_REFRESH, unifyOrigin: window.location.href }, activeView?.url || activeFrm?.src);
  };

  const openAmplitudeRC = (): void => {
    const activeView = getActiveIframe();
    const activeFrm: any = document.getElementById(activeView?.id || '');
    postMessage(activeFrm, 
        { 
          id: activeView?.id, 
          action: MESSAGES.UNIFY_AMPLITUDE_RC_OPEN, 
          unifyOrigin: window.location.href 
        }, activeView?.url || activeFrm?.src);
  };

  // ─── Message handlers ───────────────────────────────────────────────────────

  const handleClientReady = (id: string, evt: MessageEvent): void => {
    const frameEl = getVerifiedFrame(id, evt);
    if (!isReloadNavigation() || !frameEl) return;
    if (refreshNotifiedRef.current.has(id)) return;

    refreshNotifiedRef.current.add(id);
    const view = webviewsRef.current.find((wv) => wv.id === id);
    const liveAppDomains = useChannelMessagingStore.getState().appDomains;
    const lastVisitedUrl = view?.visitedOrigin && view?.visitedPath
      ? view.visitedOrigin + view.visitedPath
      : undefined;
    // Trust the previously-registered app domain / cached visited origin over
    // evt.origin alone — evt.origin is legitimate here (browser-set, not
    // spoofable), but we still prefer known-good state when available.
    const appOrigin = view?.visitedOrigin || liveAppDomains[id] || evt.origin;
    const targetOrigin = resolveTargetOrigin(appOrigin);

    if (targetOrigin) {
      postMessage(frameEl, {
        id,
        action: MESSAGES.UNIFY_REFRESH,
        unifyOrigin: window.location.href,
        visitedOrigin: view?.visitedOrigin,
        visitedPath: view?.visitedPath,
        lastVisitedUrl,
      }, lastVisitedUrl || targetOrigin);
    }

    const restoreUrl = view ? getRestoreUrlForView(view) : null;
    if (restoreUrl) {
      console.log(`[cm] Restoring URL for ${id}:`, restoreUrl);
      if (!urlsMatch(frameEl.src, restoreUrl)) frameEl.src = restoreUrl;
      const visitedPath = extractPathFromUrl(restoreUrl);
      if (visitedPath) setVisitedLocation(id, visitedPath, extractOriginFromUrl(restoreUrl));
    }
  };

  const handleInitConnection = (evt: MessageEvent): void => {
    const { id, origin } = evt.data;
    const frameEl = getVerifiedFrame(id, evt);
    if (!frameEl) return;

    // evt.origin is set by the browser and cannot be spoofed by the sender,
    // unlike evt.data.origin. Use it as the source of truth for the app's
    // domain mapping rather than trusting the payload's self-reported origin.
    const trustedOrigin = evt.origin || origin;
    if (!trustedOrigin) return;

    setAppDomains({ ...appDomains, [id]: trustedOrigin });
    initClient(frameEl, id, trustedOrigin);
  };

  const lastIndexRef = useRef<Record<string, number>>({});
  const initialIndexRef = useRef<Record<string, number>>({});

  const handleLocationChanged = (id: string, evt: MessageEvent): void => {
    const urlFromLocation = evt.data?.origin || evt.data?.value;
    const navigationType = evt.data?.navigationType;
    const index = typeof evt.data?.index === 'number' ? evt.data.index : undefined;

    if (typeof urlFromLocation !== 'string' || !urlFromLocation) return;

    const isActive = useWebViewStore.getState().webviews.find((w) => w.id === id)?.active ?? false;
    if (navigationType === 'initial' && typeof index === 'number' && initialIndexRef.current[id] === undefined) {
      initialIndexRef.current[id] = index;
    }

    const initialIndex = initialIndexRef.current[id] ?? 0;

    // ─── Index-based depth tracking, replacing the landing-path heuristic ──
    // The Navigation API's currentEntry.index is a monotonic depth within
    // the child's own session history: 0 is always its root/landing entry.
    // Comparing it to the last known index tells us direction (traverse is
    // ambiguous on its own — it fires for both back AND forward, and can
    // jump more than one step at a time), and reaching 0 via a backward
    // traverse is a reliable "the app has been backed out to its root"
    // signal.
    //
    // This bookkeeping runs unconditionally, before the isActive gate below:
    // a backgrounded app can still navigate (e.g. a silent token-refresh
    // redirect), and if we only updated lastIndexRef while active,
    // reactivating it later would compare against a stale baseline.
    const prevIndex = lastIndexRef.current[id];
    let reachedRootViaBack = false;
    if (typeof index === 'number') {
        reachedRootViaBack =
            navigationType === 'traverse' &&
            typeof prevIndex === 'number' &&
            index < prevIndex &&
            index === 0;
      lastIndexRef.current[id] = index;
    }

    if (!isActive) return;
    
    if (lastReportedUrlRef.current[id] === urlFromLocation) return;
      lastReportedUrlRef.current[id] = urlFromLocation;

    console.log("[cm] location changed:",urlFromLocation);
    console.log("[cm] location index:",index);
    console.log("[cm] reachedRootViaBack:",reachedRootViaBack);

    setIndex(id,index);
    if(initialIndex === index && urlFromLocation.indexOf("login") === -1){
      setIndex(id,-1);
    }

    if (reachedRootViaBack) {
      const shellNav = window.navigation;
      const shellEntries = shellNav?.entries() ?? [];
      const currentIndex = shellNav?.currentEntry?.index ?? 0;
      const previousEntry = shellEntries[currentIndex - 1] as any;

      if (previousEntry?.url) {
        const p = new URL(previousEntry.url);
        resetActive();
        history.replace({ pathname: p.pathname, search: p.search });
      } else {
        console.warn(`[cm] ${id} — no prior shell entry, falling back to a known-safe route`);
        resetActive();
        history.replace('/unify/applications');
      }
      return;
    }

    const newPath = extractPathFromUrl(urlFromLocation);
    if (!newPath) return;
 
    const newPathname = newPath.split('?')[0];
    setVisitedLocation(id, newPath, extractOriginFromUrl(urlFromLocation));
    captureEvent({ name: pathToBreadcrumb(newPathname), url: urlFromLocation, action: 'Navigated' });
      
  };

  // -------------- handle deep link -------------------
  const setContextUrl = useChannelMessagingStore((state:any) => state.setContextUrl );

  const handleDeepLink = (evt: MessageEvent) => {
    try{
      const urlFromLocation = evt.data?.origin || evt.data?.value;
        if (typeof urlFromLocation === 'string' && urlFromLocation.length > 0) {
        setContextUrl(urlFromLocation);    
        const activeView = getActiveIframe();
        if (activeView && activeView.id === evt.data?.id) {
            let linkPath = window.location.pathname;
            linkPath = window.location.pathname.indexOf('/dp/') >= 0 ? (window.location.pathname.split('/dp/')[0] || '') : linkPath;
            linkPath = window.location.pathname.indexOf('/bdp') >= 0 ? (window.location.pathname.split('/bdp')[0] || '') : linkPath;  
            const paramHash = btoa(JSON.stringify({id: evt.data?.id, url: urlFromLocation }));
            window.history.replaceState({}, '', linkPath + `/bdp` + paramHash);
          }
        }
    } catch (err: any) {
      console.warn('Unify Messaging - handleDeepLink Error', err.stackTrace);
    }
  };

  // ─── Central dispatcher ─────────────────────────────────────────────────────

  const messageHandler = (evt: MessageEvent): void => {
    try {
      const { id, action } = evt.data || {};
      if (!id) return;

      // Global source-of-truth check: every action below (including
      // REDIRECT_COMPLETE / UNIFY_TOKEN_REQUEST) must come from the window
      // we actually mounted as iframe `id`. evt.data.id and evt.data.origin
      // are attacker-controllable; evt.source is not.
      const frameEl = getVerifiedFrame(id, evt);
      if (!frameEl) return;

      switch (action) {
        case MESSAGES.UNIFY_CLIENT_READY:           handleClientReady(id, evt);    break;
        case MESSAGES.UNIFY_CLIENT_INIT_CONNECTION: handleInitConnection(evt);     break;
        case MESSAGES.APP_LOCATION_CHANGED:         handleLocationChanged(id, evt); handleDeepLink(evt); break;
        case MESSAGES.UNIFY_TOKEN_REQUEST:          postTokenMessage(id, evt.origin, unifyOrigin); break;
        case MESSAGES.RIGHT_CLICK:                  handleLatestRightClick(evt);  break;
      }
      Object.values(listenersMap.current).forEach((listener) => listener?.(evt));
    } catch (err: any) {
      console.warn('Unify Messaging - messageHandler Error', err.stackTrace);
    }
  };

  // ─── Latest Right Click Handler ─────────────────────────────────────────────

  const handleLatestRightClick = (evt: MessageEvent): void => {
    const { origin, rcLink, id } = evt.data || {};
    try {
      // add to right click registry 
      const currentStack = useChannelMessagingStore.getState().rightClickStack;
      origin && setRightClickStack([...currentStack, {time: Date.now(), rcLink, origin, id}]);
    } catch (err: any) {
      console.warn('Unify Messaging - handleLatestRightClick Error', err.stackTrace);
    }
  };

  // ─── Logout ─────────────────────────────────────────────────────────────────

  const endPointLogout = async (): Promise<void> => {
    try {
      setLogoutStatus(LOGOUT_STATUS.LOGOUT_PROGRESS);
      setTimeout(() => {
        webviews
          .filter((wv: any) => wv.logoutUrl)
          .forEach((product: any) => {
            const frame = document.getElementById(product.id) as HTMLIFrameElement;
            if (frame) frame.src = product.logoutUrl;
            else console.warn(`Could not find iframe: ${product.id}`);
          });
      }, 100);
      await new Promise((resolve) => setTimeout(resolve, 5000));
      setLogoutStatus(LOGOUT_STATUS.APPS_COMPLETE);
    } catch (err: any) {
      console.warn('Unify Messaging - endPointLogout', err.stackTrace);
    }
  };

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => { webviewsRef.current = webviews; }, [webviews]);

  // NOTE: no reactivation-sync effect here. WebViewManager keeps every
  // webview's iframe permanently mounted — switching `active` only toggles
  // CSS visibility (see WebViewWrapper), it never unmounts, reloads, or
  // reassigns `frameEl.src`. So a backgrounded app's route never drifts;
  // there's nothing to restore on tab switch. navigateFrame is still exposed
  // via context for genuine shell-initiated deep-linking (a notification, a
  // menu item, a breadcrumb) — driving an already-mounted app to a route the
  // user hasn't organically navigated to — just not for tab-switch sync.

  useEffect(() => {

    const listener = (evt: MessageEvent) => messageHandler(evt);
    window.addEventListener('message', listener);
    return () => window.removeEventListener('message', listener);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMsgApps]);

  useEffect(() => {
    const renewedHandler = (key: string, newToken: any) => {
      if (key === 'accessToken' && newToken?.accessToken) {
        const stored = readOktaTokenStorage();
        notifyMessageRef.current(MESSAGES.UNIFY_TOKEN_RESPONSE, {
          accessToken: newToken,
          idToken: stored?.idToken,
          renewed: true,
        });
      }
    };
    oktaAuth?.tokenManager?.on('renewed', renewedHandler);
    return () => oktaAuth?.tokenManager?.off('renewed', renewedHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaAuth]);

  // ======================= VIS special case ======================
  const validateVISRoute = (route:string) => {
    try{
      let visURL = null;
      let rcUrl = '';
      try{ visURL = new URL(route); }catch(e){};
      if(visURL) {
        const rcLink = visURL?.origin + decodeURIComponent(route)?.split('redirectPath=')[1];
        let rcMatch = rightClickStack.find((rc:any) => rc.rcLink === rcLink);
        if(rcMatch) rcUrl = rcMatch?.rcLink;
      }
      return rcUrl;
    } catch(_){
      return '';
    }
  }

  // ===============================================================

  useEffect(() => {
     const unifyChannel = new BroadcastChannel('unify_bc');
    unifyChannel.onmessage = (event) => {
      try{ 
        const { id, action, origin, routeFrom} = event.data || {};
        if (id !== 'UNIFY_BC' || !action || origin !== window.location.origin) return;
        
        if(action === 'GET_LATEST_RIGHTCLICK') {
          const visLink = validateVISRoute(routeFrom);
          const rcMatch = rightClickStack.find((rc:any) => rc.rcLink === routeFrom || rc.rcLink === visLink);
        
          if(rcMatch) {
            // if request path and right clicks path matched
            unifyChannel.postMessage({ id: 'UNIFY_BC', action: 'LATEST_RIGHTCLICK', rcLink: rcMatch?.rcLink || visLink, origin: window.location.href, value: rcMatch }); 
          }
          let newRcStack = rightClickStack;
          // clear only one matched link, there might be multiple right clicks openes on same link
          const matchIndex = rightClickStack.findIndex((rc:any) => rc.rcLink === routeFrom || rc.rcLink === visLink );
          newRcStack = rightClickStack.filter((_: any, index: number) => index !== matchIndex);
          // clear registred right clicks happened beyoud 15 secs
          newRcStack = newRcStack.filter((rc:any) => (Date.now() - rc.time) < 20000); 
          setRightClickStack(newRcStack);
        }
       }catch(e){console.warn(e);}
    };
    return () => unifyChannel.close();
    // eslint-disable-next-line
  }, [rightClickStack]);

// ─── Broadcast Channel Messaging ──────────────────────────────────────────────





// ─── Provider ─────────────────────────────────────────────────────────────────

  return (
    <MessageContext.Provider
      value={{
        setLogoutStatus,
        logoutStatus,
        channelMessageEnabled,
        getWorkflowAppId,
        initClient,
        postMessage,
        addMessageListener,
        refreshActiveFrame,
        notifyMessage,
        endPointLogout,
        openAmplitudeRC,
      }}
    >
      {props.children}
    </MessageContext.Provider>
  );
}